import { Instance } from 'mobx-state-tree';
import * as React from 'react';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { TLCode } from '../../common/enums';
import { DutyAssignment } from '../../store/models/DutyAssignment';
import { ColDef } from 'ag-grid-community';
import { Button, Colors } from '@blueprintjs/core';
import { MAP } from '@blueprintjs/icons/lib/esm/generated/iconNames';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import BaseGridXL from '../common/grid/BaseGridXL';
import styled from '@emotion/styled';
import { dateFilterNormal, renderGridDateWithDay } from '../../store/utils';
import { DateInput, IDateFormatProps } from '@blueprintjs/datetime';
import { IconNames } from '@blueprintjs/icons';
import { PharmacyDetails } from '../../store/models/PharmacyDetails';
import DataGrid, {
  Column,
  FilterRow,
  FilterPanel,
  Grouping,
  GroupPanel,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
  Export,
} from 'devextreme-react/data-grid';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { UserStore, UiStore } from '../../store';

interface PharmacyDutiesListProps extends WithTranslateProps {
  duties: Array<Instance<typeof DutyAssignment>>;
  pharmacyDetails?: Instance<typeof PharmacyDetails>;
  groupMode: boolean;
  UserStore?: Instance<typeof UserStore>;
  UiStore?: Instance<typeof UiStore>;
  onYearChange?: () => void;
}

interface PharmacyDutiesListState {
  searchDate: Date;
}

const DivLeft = styled.div`
  display: flex;
  flex-direction: row;
  margin-right: 5%;
  padding-bottom: 5px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const CenterContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

@observer
@inject('UserStore')
@inject('UiStore')
class PharmacyDutiesList extends React.Component<PharmacyDutiesListProps, PharmacyDutiesListState> {
  state = {
    searchDate: moment()
      .year(this.props.UiStore!.pharmacyFirstLineYear)
      .toDate(),
  } as PharmacyDutiesListState;

  onExporting = (e) => {
    this.onExportingToExcel(e, '-Gardes-Groupe- ');
  };

  onExportingToExcel = (e, name) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');
    exportDataGrid({
      component: e.component,
      worksheet: worksheet,
      customizeCell: function(options) {
        options.excelCell.font = { name: 'Arial', size: 12 };
        options.excelCell.alignment = { horizontal: 'left' };
      },
    }).then(function() {
      workbook.xlsx.writeBuffer().then(function(buffer) {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Pharmagarde' + name + '.xlsx');
      });
    });
  };
  handleDateChange = (date: Date) => {
    this.setState({ searchDate: date });
  };

  columns = [
    {
      headerName: this.props.translate(TLCode.PHARMACY_DUTIES_Date),
      field: 'date',
      width: 100,
      sort: 'asc',
      sortingOrder: 1,
      cellRenderer: renderGridDateWithDay,
      filterParams: {
        textCustomComparator: dateFilterNormal,
      },
    },
    {
      headerName: this.props.translate(TLCode.PHARMACY_DUTIES_Hour),
      field: 'dutyType',
      width: 100,
      sort: 'desc',
      sortingOrder: 2,
    },
    {},
  ] as ColDef[];

  buildGridData = () => {
    const start = new Date(this.state.searchDate.getFullYear(), 0, 1);
    const end = new Date(this.state.searchDate.getFullYear() + 1, 0, 1);
    const userStoreList = this.props.UserStore && this.props.UserStore.dataList ? this.props.UserStore.dataList : [];
    return this.props.duties
      .filter((d) => new Date(d.duty!.dutyDate.date) >= start && new Date(d.duty!.dutyDate.date) <= end)
      .map((d) => {
        const pharmacyUser = userStoreList.find((x) => x.userName === d.pharmacySimple!.apbNumber.toString());

        return {
          id: d.dutyAssignmentId,
          dutyType: this.props.translate(d.duty!.dutyType!.type === 'DAY' ? TLCode.DutyTypeDay : TLCode.DutyTypeNight),
          dutyId: d.duty!.dutyId,
          date: d.duty!.dutyDate.date,
          pharmacyName: d && d.pharmacySimple ? d.pharmacySimple.name : '',
          pharmacyApbNumber: d && d.pharmacySimple ? d.pharmacySimple.apbNumber : '',
          pharmacyAddress: d && d.pharmacySimple ? d.pharmacySimple.address : '',
          pharmacyCity: d && d.pharmacySimple ? d.pharmacySimple.city : '',
          pharmacyZip: d && d.pharmacySimple ? d.pharmacySimple.zipCode : '',
          pharmacyPhoneNumber: d && d.pharmacySimple ? d.pharmacySimple.phone : '',
          pharmacyEmail: pharmacyUser && pharmacyUser.email ? pharmacyUser.email : '',
        };
      });
  };

  toggleColor = (color) => {
    if (color == Colors.WHITE) {
      return Colors.LIGHT_GRAY4;
    } else {
      return Colors.WHITE;
    }
  };

  getPharmacistEmail = (row) => {
    const user = this.props.UserStore!.dataList.find((user) => user.userName == row.pharmacyApbNumber);
    return user ? user!.email! : '';
  };

  render() {
    let backgroundColor = Colors.WHITE;
    const jsDateFormatter: IDateFormatProps = {
      // note that the native implementation of Date functions differs between browsers
      formatDate: (date) => {
        date.setDate(1);
        date.setMonth(0);
        return date.getFullYear().toString();
      },
      parseDate: (str) => new Date(str),
      placeholder: 'M/D/YYYY',
    };
    const minDate = new Date('01/01/2019');
    const maxDate = new Date('01/01/2029');
    const { pharmacyDetails, onYearChange } = this.props;

    return (
      <>
        {' '}
        <Container>
          <b>
            ({pharmacyDetails!.level})&nbsp;&nbsp;{pharmacyDetails!.apbNumber}&nbsp;-&nbsp;{pharmacyDetails!.name}
            &nbsp;-&nbsp;{pharmacyDetails!.address},&nbsp;
            {pharmacyDetails!.zipCode}&nbsp;
            {pharmacyDetails!.city}
          </b>
          <br />
          <DivLeft>
            <Button
              icon={IconNames.CHEVRON_LEFT}
              small={true}
              style={{ height: '30px', marginRight: '5px' }}
              onClick={() => {
                const date = moment(this.state.searchDate).subtract(1, 'y');
                this.setState({ searchDate: date.toDate() });
                this.props.UiStore!.setPharmacyCalendarYear(date.year());
                if (onYearChange !== undefined) onYearChange();
              }}
              disabled={this.state.searchDate.getFullYear() === 2019}
            />
            <DateInput
              value={this.state.searchDate}
              {...jsDateFormatter}
              minDate={minDate}
              maxDate={maxDate}
              onChange={this.handleDateChange}
            />
            <Button
              icon={IconNames.CHEVRON_RIGHT}
              small={true}
              style={{ height: '30px', marginLeft: '5px' }}
              onClick={() => {
                const date = moment(this.state.searchDate).add(1, 'y');
                this.setState({ searchDate: date.toDate() });
                this.props.UiStore!.setPharmacyCalendarYear(date.year());
                if (onYearChange !== undefined) onYearChange();
              }}
              disabled={this.state.searchDate.getFullYear() === maxDate.getFullYear()}
            />
          </DivLeft>
          {!this.props.groupMode && (
            <>
              <BaseGridXL
                columnDefs={this.columns}
                rowData={this.buildGridData()}
                keyName={'id'}
                getRowStyle={(params) => {
                  console.log(params);
                  var year = new Date().getFullYear() + 1;
                  const currDate = params.api.getDisplayedRowAtIndex(params.node.rowIndex).data.date;
                  if (currDate.includes(year + '-01-01T00:00:00')) {
                    return {
                      'background-color': Colors.VERMILION5,
                    };
                  }
                  if (params.node.rowIndex == 0) {
                    return {
                      'background-color': backgroundColor,
                    };
                  } else {
                    const prevDate = params.api.getDisplayedRowAtIndex(params.node.rowIndex - 1).data.date;
                    if (prevDate != currDate) {
                      backgroundColor = this.toggleColor(backgroundColor);
                    }
                    return {
                      'background-color': backgroundColor,
                    };
                  }
                }}
              />
            </>
          )}
          {this.props.groupMode && (
            <DataGrid
              dataSource={this.buildGridData()}
              allowColumnReordering={true}
              rowAlternationEnabled={true}
              showBorders={true}
              width="100%"
              allowColumnResizing={true}
              columnResizingMode={'widget'}
              onExporting={this.onExporting}
            >
              <FilterRow visible={true} applyFilter={'auto'} />
              <FilterPanel visible={true} />
              <HeaderFilter visible={true} />
              <GroupPanel visible={true} />
              <SearchPanel visible={true} highlightCaseSensitive={true} width={300} />
              <Grouping autoExpandAll={false} />
              <Export enabled={true} />
              <Column
                dataField="date"
                caption={this.props.translate(TLCode.TOO_CLOSE_DUTIES_DATE1)}
                minWidth={90}
                width={150}
                dataType="date"
                format="dd/MM/yyyy"
                defaultSortOrder="asc"
                sortIndex={1}
              />
              <Column
                dataField="dutyType"
                caption={this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY_GROUP)}
                minWidth={120}
                width={120}
                defaultSortOrder="desc"
                sortIndex={2}
              />
              <Column
                dataField="pharmacyApbNumber"
                caption={this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY)}
                minWidth={70}
                width={90}
              />
              <Column
                dataField="pharmacyName"
                caption={this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY_NAME)}
                minWidth={250}
                width={500}
              />
              <Column
                dataField="pharmacyCity"
                caption={this.props.translate(TLCode.PHARMACIES_City)}
                minWidth={120}
                width={120}
              />
              <Column
                dataField="pharmacyZip"
                caption={this.props.translate(TLCode.PHARMACIES_ZipCode)}
                minWidth={120}
                width={120}
              />
              <Column
                dataField="pharmacyPhoneNumber"
                caption={this.props.translate(TLCode.PHARMACY_Phone)}
                minWidth={120}
                width={120}
              />
              <Column
                dataField="pharmacyEmail"
                caption={this.props.translate(TLCode.Email)}
                minWidth={120}
                width={120}
              />
              <Pager
                visible={true}
                allowedPageSizes={[5, 10, 20, 50, 100]}
                showPageSizeSelector={true}
                showInfo={true}
                showNavigationButtons={true}
              />
              <Paging defaultPageSize={20} />
            </DataGrid>
          )}
        </Container>
      </>
    );
  }
}

export default withTranslate(PharmacyDutiesList);
